import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { IDoesFilterPassParams } from 'ag-grid-community';
import IconButton from 'lib/IconButton';
import { compact, debounce } from 'lodash';
import Button, { Color, Size } from 'lib/Button';
import closeSvg from 'assets/icons/close.svg';
import { FrameIdFilterProps, FrameIdFilterRef } from './types';

const parseFilterText = (text: string): string[] => {
  if (!text.trim()) return [];

  return compact(text.split(/[,\n;]+/).map((id) => id.trim()));
};

export const FrameIdFilter = forwardRef<FrameIdFilterRef, FrameIdFilterProps>(({ api, filterChangedCallback }, ref) => {
  const [filterText, setFilterText] = useState<string>('');
  const [filterValues, setFilterValues] = useState<string[]>([]);

  const debouncedFilterChanged = useRef(
    debounce(() => {
      filterChangedCallback?.();
    }, 300),
  ).current;

  useEffect(() => {
    const values = parseFilterText(filterText);
    setFilterValues(values);
  }, [filterText]);

  useEffect(() => {
    return () => {
      debouncedFilterChanged.cancel();
    };
  }, [debouncedFilterChanged]);

  useImperativeHandle(
    ref,
    () => ({
      doesFilterPass(params: IDoesFilterPassParams): boolean {
        if (filterValues.length === 0) return true;

        const frameId = params.data?.frameId;
        if (!frameId) return false;

        const lowerFrameId = String(frameId).toLowerCase();
        return filterValues.some((filterValue) => lowerFrameId.includes(filterValue.toLowerCase()));
      },

      isFilterActive(): boolean {
        return filterValues.length > 0;
      },

      getModel() {
        return filterValues.length === 0
          ? null
          : {
              filterType: 'frameId',
              values: filterValues,
            };
      },

      setModel(model) {
        const values = model?.values ?? [];
        setFilterText(values.join(', '));
        setFilterValues(values);
      },
    }),
    [filterValues],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setFilterText(e.target.value);
    debouncedFilterChanged();
  };

  const handleClear = (): void => {
    setFilterText('');
    filterChangedCallback?.();
  };

  const handleClose = (): void => {
    if (api && typeof api.hidePopupMenu === 'function') {
      api.hidePopupMenu();
    }
  };

  return (
    <div className="p-4 pr-5 bg-white relative">
      <IconButton name="close" icon={closeSvg} handleOnClick={handleClose} className="justify-self-end" />

      <div className="mb-2">
        <label htmlFor="frame-id-filter-input" className="block text-sm font-semibold text-neutral-800 mb-1">
          Filter frame IDs
        </label>
        <p className="text-xs text-neutral-600 mb-2">Search and filter rows by Frame ID</p>
        <textarea
          id="frame-id-filter-input"
          value={filterText}
          onChange={handleInputChange}
          placeholder="Paste Frame IDs here"
          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          rows={7}
          style={{ resize: 'vertical', minHeight: '120px' }}
        />
        {filterValues.length > 0 && (
          <div className="mt-1 text-xs text-neutral-600">
            {filterValues.length} search term{filterValues.length !== 1 ? 's' : ''} entered
          </div>
        )}
      </div>

      <div className="flex mb-2">
        <Button classNames="ml-auto" label="Clear" size={Size.MEDIUM} color={Color.PRIMARY} onClick={handleClear} />
      </div>

      <div className="pt-3 border-t border-neutral-300">
        <div className="text-xs text-neutral-600 leading-relaxed">
          <div className="list-disc list-inside space-y-0.5 ml-1">
            <p>Paste multiple IDs (comma, semicolon, or new line separated)</p>
            <p>Shows rows matching any of the IDs</p>
          </div>
        </div>
      </div>
    </div>
  );
});
